import React, { useEffect, useLayoutEffect } from "react"
import { useHistory, useLocation, useParams } from "react-router-dom"
import {
  EventCounts, EventGroup, ModalName, Segment, Table, AccountUser, DioboxEvent,
} from "sharedTypes"
import EventPage, { EventPageHeader } from "components/layout/EventPage"
import Filters from "pages/EventShow/Guests/ScrollView/Filters"
import usePermissions from "services/permissions"
import GuestStatBar from "pages/EventShow/Guests/GuestStatBar"
import CapacityStat from "pages/EventShow/Forms/CapacityStat"
import Menu, { MenuDivider, MenuItem } from "components/dialogs/Menu"
import { useGuests } from "queries/guests"
import { useShallow } from "zustand/shallow"
import useGuestsStore from "services/useGuestsStore"
import useModal from "services/useModal"
import useSearchParams from "services/useSearchParams"
import { eventPath, useEventGuestPaths } from "utilities/routes"
import { serializeFilters, useUrlFilters } from "utilities/filterRulesUrl"
import { Box, HStack } from "@chakra-ui/react"
import useFeatureFlag from "services/featureFlags"
import { isSegmentDirty, isSelectAllBlocked } from "utilities/chipRules"
import NoGuests from "./NoGuests"
import LimitReached from "./LimitReached"
import GuestsView from "./GuestsView"
import ViewModeMenu from "./ViewModeMenu/ViewModeMenu"
import ColumnVisibilityMenu from "./ViewModeMenu/ColumnVisibilityMenu"
import Searchbar from "./TableView/Searchbar"
import GuestFilters from "./TableView/Filters"
import { ColumnVisibilityProvider } from "./ColumnVisibilityProvider"
import useGuestsViewSettings, { type ViewMode, type SortOrder } from "./useGuestsViewSettings"

interface Props {
  counts: EventCounts
  currency: string
  segmentsEnabled: boolean
  enhanceEnabled: boolean
  groupsEnabled: boolean
  eventGroups: EventGroup[]
  importLimit: number|null
  segments: Segment[]
  tables: Table[]
  timezone: string
  event: DioboxEvent
  accountUsers?: AccountUser[]
}

const Guests = ({
  counts,
  counts: { guests: guestsCount },
  currency,
  segmentsEnabled,
  enhanceEnabled,
  groupsEnabled,
  eventGroups,
  importLimit,
  segments,
  tables,
  timezone,
  event,
  accountUsers,
}: Props) => {
  const { eventId, guestId } = useParams<{ eventId: string, guestId: string }>()
  const history = useHistory()
  const location = useLocation()
  const showModal = useModal()
  const { canEditGuests, canViewForm } = usePermissions()
  const {
    guestsTableView: guestsTableViewEnabled, eventCapacity: eventCapacityEnabled,
  } = useFeatureFlag()
  const {
    groupId: groupIdSearchParam,
    segmentId: segmentIdSearchParam,
    rules: rulesSearchParam,
    q,
  } = useSearchParams()
  const {
    searchString,
    groupId,
    segmentId,
    order,
    selectAll,
    deselectAll,
    allSelected,
    selectedGuestIds,
    reset,
    filter,
    sort,
    setSelectAllBlocked,
  } = useGuestsStore(
    useShallow((state) => ({
      searchString: state.searchString,
      groupId: state.groupId,
      segmentId: state.segmentId,
      order: state.order,
      selectAll: state.selectAll,
      deselectAll: state.deselectAll,
      allSelected: state.allSelected,
      selectedGuestIds: state.selectedGuestIds,
      reset: state.reset,
      filter: state.filter,
      sort: state.sort,
      setSelectAllBlocked: state.setSelectAllBlocked,
    })),
  )
  const filters = useUrlFilters()

  const {
    viewMode, setViewMode,
    showStatusSummary, setShowStatusSummary,
    sortOrder, setSortOrder,
  } = useGuestsViewSettings()

  const handleSetOrder = (newOrder: string) => {
    sort(newOrder)
    setSortOrder(newOrder as SortOrder)
  }

  const shouldUseTableView = viewMode === "table" && guestsTableViewEnabled

  // Hard reload on a guest route in table view: no intercepted background exists,
  // so we set up background state and let EventShow's generic mechanism handle the modal.
  const needsBackgroundRedirect = shouldUseTableView
    && !!guestId
    && !location.state?.background

  useLayoutEffect(() => {
    if (needsBackgroundRedirect) {
      history.replace({
        ...location,
        state: {
          background: {
            pathname: `${eventPath(eventId)}/guests`,
            search: location.search,
          },
        },
      })
    }
  }, [needsBackgroundRedirect])

  const toggleSelectAll = () => (allSelected ? deselectAll : selectAll)()

  // Classic (list) view has no chip-filter UI at all, unlike a selected
  // Guest List / segment (groupId/segmentId), which it already supports via
  // its own sidebar picker. So only ad-hoc chip filters (`rules=`) need to be
  // dropped here — confirmed first, since it's a destructive, easy-to-miss
  // side effect of switching views.
  const handleSetViewMode = (newViewMode: ViewMode) => {
    // When switching from list to table view with a guest selected,
    // navigate to the base guests path to avoid opening the modal automatically
    if (viewMode === "list" && newViewMode === "table" && guestId) {
      history.push(eventGuestsPath(eventId))
    }
    if (viewMode === "table" && newViewMode === "list" && filters.length > 0) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        "Classic View doesn't support filters yet. Switching will remove your current filters.",
      )
      if (!confirmed) return
      const parts: string[] = []
      if (groupId) parts.push(`groupId=${groupId}`)
      if (segmentId) parts.push(`segmentId=${segmentId}`)
      if (searchString) parts.push(`q=${encodeURIComponent(searchString)}`)
      history.push(eventGuestsPath(eventId, parts.join("&")))
    }
    setViewMode(newViewMode)
  }

  useEffect(() => {
    reset()
    if (sortOrder) sort(sortOrder)
  }, [])

  useEffect(() => {
    filter({
      groupId: groupIdSearchParam,
      segmentId: segmentIdSearchParam,
      searchString: q,
    })
  }, [groupIdSearchParam, segmentIdSearchParam, q])

  // When currently selected Segment is deleted filters are reset
  const selectedSegment = segments.find(({ id }) => id === segmentId)
  useEffect(() => {
    if (segmentId && !selectedSegment) {
      reset()
    }
  }, [selectedSegment])

  // Bulk actions only know saved segments and guest lists, not a search or unsaved chips
  const selectAllBlocked = isSelectAllBlocked(searchString, filters, selectedSegment)
  useEffect(() => {
    setSelectAllBlocked(selectAllBlocked)
  }, [selectAllBlocked])

  const {
    guests,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
  } = useGuests(eventId, searchString, order, groupId, segmentId, filters)
  const { eventGuestsPath } = useEventGuestPaths()

  const multipleSelectEnabled = !!selectedGuestIds.length

  useEffect(() => {
    if (guestId) {
      deselectAll()
    }
  }, [guestId])

  useEffect(() => {
    if (multipleSelectEnabled || allSelected) {
      history.push(eventGuestsPath(eventId))
    }
  }, [multipleSelectEnabled, allSelected])

  useEffect(() => {
    deselectAll()
  }, [groupId, segmentId, rulesSearchParam])

  useEffect(() => {
    if (searchString !== "") {
      deselectAll()
    }
  }, [searchString])

  if (needsBackgroundRedirect) {
    return null
  }

  if (!accountUsers) {
    return null
  }

  if (!guestsCount) {
    return <NoGuests />
  }

  if (importLimit && remainingGuestSpots(guestsCount, importLimit) < 0) {
    return <LimitReached guestLimit={importLimit} guestsCount={guestsCount} />
  }

  // Sidebar segment / guest-list selection. Picking a new axis (group or
  // segment) replaces the previous filter context AND drops any chip set —
  // but preserves the active search query, so typing "John" then clicking
  // a different segment still shows John-shaped guests in that segment.
  const onFilter = (f) => {
    if (f.groupId !== groupId || f.segmentId !== segmentId) {
      const parts: string[] = []
      if (f.groupId) parts.push(`groupId=${f.groupId}`)
      if (f.segmentId) parts.push(`segmentId=${f.segmentId}`)
      if (searchString) parts.push(`q=${encodeURIComponent(searchString)}`)
      history.push(eventGuestsPath(eventId, parts.join("&")))
    }
  }

  // Search bar input. Coexists with chip filters via `?rules=&q=` — the
  // API intersects that path. Coexists with `?segmentId=` / `?groupId=`
  // alone via legacy "search wins" semantics — until DIO-931 makes the BE
  // intersect those too, picking Checked In + searching shows results
  // across the whole event. `NoGuestsMatch` handles the messaging
  // difference (filter-aware copy only when chips are present).
  const handleSearch = (query) => {
    const parts: string[] = []
    if (groupId) {
      parts.push(`groupId=${groupId}`)
    } else {
      if (segmentId) parts.push(`segmentId=${segmentId}`)
      if (filters.length > 0) parts.push(`rules=${serializeFilters(filters)}`)
    }
    if (query) parts.push(`q=${encodeURIComponent(query)}`)
    history.push(eventGuestsPath(eventId, parts.join("&")))
  }

  const filtered = !!groupId || !!segmentId || !!searchString || filters.length > 0
  // "Filtered beyond search" — used by the no-results UI to decide between
  // the search-only / filters-only / combined messaging. Doesn't include
  // searchString since that's the orthogonal axis.
  const filtersActive = !!groupId || !!segmentId || filters.length > 0
  // Clears the filter axis while preserving the active search query —
  // pushes a URL with just `?q=...` (or nothing). The store hydration
  // loop drops segmentId / groupId / rules on the next render.
  const clearFilters = () => {
    history.push(eventGuestsPath(eventId, searchString ? `q=${encodeURIComponent(searchString)}` : ""))
  }

  const content = (
    <EventPage
      scrollableBody={false}
      header={(
        <EventPageHeader
          title={(
            <Filters
              counts={counts}
              eventGroups={eventGroups}
              segmentsEnabled={segmentsEnabled}
              segments={segments}
              onChange={onFilter}
              selected={{ segmentId, groupId }}
              canEditGuests={canEditGuests}
              teamMembers={accountUsers}
              // "*" + "unsaved" suffix only fires for the one case where
              // there's an in-place save path: a custom segment whose
              // persisted rules differ from the chip row. Preset divergence
              // can't be saved-in-place (no PUT) — it surfaces via the
              // `isUnsavedView` branch below instead.
              isViewDirty={!!(
                segmentId
                && filters.length > 0
                && isSegmentDirty(
                  segments.find((s) => s.id === segmentId), filters,
                )
              )}
              // "Unsaved Segment" replaces the picked identity's label any
              // time the chip row represents something new the user could
              // POST. That's: no identity at all, OR an identity that
              // can't be modified (default group preset, event group) but
              // the chips have diverged. Picking "Confirmed" then editing
              // a chip puts you here — the title stops pretending you're
              // still on Confirmed.
              isUnsavedView={
                filters.length > 0 && !segmentId
              }
            />
          )}
          {... shouldUseTableView && {
            subtitle: (
              <HStack pt={6} pb={4} minH={20} gap={4} alignItems="center">
                <Searchbar
                  loading={isLoading}
                  onSearch={handleSearch}
                  searchString={searchString}
                  event={event}
                />
                <GuestFilters
                  eventId={eventId}
                  segments={segments}
                  eventGroups={eventGroups}
                  segmentsEnabled={segmentsEnabled}
                />
              </HStack>
            ),
          }}
          items={(
            <>
              {eventCapacityEnabled && event.capacity && (
                <Box
                  as={canViewForm ? "button" : undefined}
                  type={canViewForm ? "button" : undefined}
                  onClick={canViewForm ? () => showModal(ModalName.EventCapacity) : undefined}
                >
                  <CapacityStat
                    capacity={event.capacity}
                    confirmedGuests={counts.confirmedGuests}
                  />
                </Box>
              )}
              <GuestStatBar counts={counts} showSummary={showStatusSummary} />
              <HStack>
                {guestsTableViewEnabled && viewMode === "table" && (
                <ColumnVisibilityMenu />
                )}
                {guestsTableViewEnabled && (
                <ViewModeMenu
                  viewMode={viewMode}
                  onSetViewMode={handleSetViewMode}
                  showStatusSummary={showStatusSummary}
                  onToggleStatusSummary={() => setShowStatusSummary(!showStatusSummary)}
                />
                )}
                {canEditGuests
              && (
              <Menu
                buttonText="Guests"
                buttonVariant="solid"
                menuItems={[
                  <MenuItem onClick={() => showModal(ModalName.NewGuest)}>
                    New Guest
                  </MenuItem>,
                  <MenuDivider />,
                  <MenuItem onClick={() => showModal(ModalName.GuestListForm)}>
                    New Guest List
                  </MenuItem>,
                  <MenuItem onClick={() => showModal(ModalName.SegmentForm)}>
                    New Segment
                  </MenuItem>,
                  <MenuDivider />,
                  <MenuItem onClick={() => showModal(ModalName.ImportGuests)}>
                    Import Guests
                  </MenuItem>,
                  <MenuItem onClick={() => showModal(ModalName.ExportGuests)}>
                    Export Guests
                  </MenuItem>,
                ].filter(Boolean)}
              />
              )}
              </HStack>

            </>
          )}
        />
      )}
      body={(
        <GuestsView
          sort={handleSetOrder}
          viewMode={!guestsTableViewEnabled ? "list" : viewMode}
          guestId={guestId}
          fetchNextPage={fetchNextPage}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          onSearch={handleSearch}
          searchString={searchString}
          order={order}
          allSelected={allSelected}
          toggleSelectAll={toggleSelectAll}
          guests={guests}
          multipleSelectEnabled={multipleSelectEnabled}
          filtered={filtered}
          filtersActive={filtersActive}
          chipsActive={filters.length > 0}
          onClearFilters={clearFilters}
          eventGroups={eventGroups}
          segments={segments}
          counts={counts}
          tables={tables}
          enhanceEnabled={enhanceEnabled}
          groupsEnabled={groupsEnabled}
          currency={currency}
          timezone={timezone}
          event={event}
          accountUsers={accountUsers}
        />
      )}
    />
  )

  if (guestsTableViewEnabled) {
    return (
      <ColumnVisibilityProvider>
        {content}
      </ColumnVisibilityProvider>
    )
  }

  return content
}

export const remainingGuestSpots = (
  guestCount: number, guestLimit: number|null,
) => {
  if (!guestLimit) {
    return Infinity
  }

  return guestLimit - guestCount
}

export default Guests
